import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    addReaction,
    createMessage,
    deleteMessage,
    listMessages,
    removeReaction,
    updateMessage,
    updateReadCursor,
} from '../../../service/ChatService';
import {mergeMessages} from '../chatUtils';

export default function useConversationTimeline({
    conversation,
    currentUser,
    invalidation,
    refreshConversations,
    readConversationMessages,
    cacheConversationMessages,
}) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState(null);
    const [atBottom, setAtBottom] = useState(true);
    const [newMessageNotice, setNewMessageNotice] = useState(false);
    const timelineRef = useRef(null);
    const atBottomRef = useRef(true);
    const conversationId = conversation?.id || 0;

    const scrollToBottom = useCallback(() => {
        const timeline = timelineRef.current;
        if (timeline) timeline.scrollTop = timeline.scrollHeight;
        atBottomRef.current = true;
        setAtBottom(true);
        setNewMessageNotice(false);
    }, []);

    const refreshMessages = useCallback(async (preserve = true, signal) => {
        if (!conversationId) return [];
        const payload = await listMessages(conversationId, {signal});
        const incoming = payload.messages || [];
        if (signal?.aborted) return incoming;
        setMessages(current => preserve ? mergeMessages(current, incoming) : incoming);
        setHasMore(incoming.length === 50);
        void cacheConversationMessages?.(conversationId, incoming);
        return incoming;
    }, [cacheConversationMessages, conversationId]);

    useEffect(() => {
        if (!conversationId) {
            setMessages([]);
            return;
        }
        let active = true;
        const abortController = new AbortController();
        setLoading(true);
        setError(null);
        atBottomRef.current = true;
        setAtBottom(true);
        setNewMessageNotice(false);
        const cached = readConversationMessages?.(conversationId) || Promise.resolve([]);
        cached.then(cachedMessages => {
            if (active && cachedMessages.length) {
                setMessages(cachedMessages);
                setLoading(false);
                window.requestAnimationFrame(scrollToBottom);
            }
            if (!active) return [];
            return refreshMessages(false, abortController.signal);
        }).then(() => {
            if (active) window.requestAnimationFrame(scrollToBottom);
        }).catch(loadError => {
            if (active && loadError?.name !== 'AbortError') setError(loadError);
        }).finally(() => active && setLoading(false));
        return () => {
            active = false;
            abortController.abort();
        };
    }, [conversationId, readConversationMessages, refreshMessages, scrollToBottom]);

    useEffect(() => {
        if (!invalidation || !conversationId) return undefined;
        const abortController = new AbortController();
        refreshMessages(true, abortController.signal).then(() => {
            if (!atBottomRef.current) setNewMessageNotice(true);
        }).catch(refreshError => {
            if (refreshError?.name !== 'AbortError') setError(refreshError);
        });
        return () => abortController.abort();
    }, [invalidation, conversationId, refreshMessages]);

    useEffect(() => {
        if (!conversation || !atBottom || document.visibilityState !== 'visible') return;
        const highest = messages.filter(message => !message.pending).at(-1)?.sequence;
        if (!highest || highest <= (conversation.last_read_root_sequence || 0)) return;
        updateReadCursor(conversation.id, highest).then(() => refreshConversations()).catch(setError);
    }, [conversation, messages, atBottom, refreshConversations]);

    useEffect(() => {
        if (!conversationId || !messages.length || !cacheConversationMessages) return undefined;
        const timer = window.setTimeout(() => {
            void cacheConversationMessages(conversationId, messages.filter(message => !message.pending));
        }, 150);
        return () => window.clearTimeout(timer);
    }, [cacheConversationMessages, conversationId, messages]);

    const loadOlder = useCallback(async () => {
        const first = messages.find(message => !message.pending)?.sequence;
        if (!conversationId || !first) return;
        const timeline = timelineRef.current;
        const oldHeight = timeline?.scrollHeight || 0;
        try {
            const payload = await listMessages(conversationId, {beforeSequence: first});
            const older = payload.messages || [];
            setMessages(current => mergeMessages(older, current));
            setHasMore(older.length === 50);
            window.requestAnimationFrame(() => {
                if (timeline) timeline.scrollTop += timeline.scrollHeight - oldHeight;
            });
        } catch (loadError) {
            setError(loadError);
        }
    }, [conversationId, messages]);

    const sendMessage = useCallback(async (body, attachments = [], retryId) => {
        if (!conversationId || !currentUser) return;
        const clientId = retryId || crypto.randomUUID();
        const pendingId = `pending-${clientId}`;
        setMessages(current => {
            const pending = {
                id: pendingId,
                conversation_id: conversationId,
                sequence: (current.at(-1)?.sequence || 0) + 1,
                sender: currentUser,
                message_type: 'USER',
                body,
                attachments,
                reactions: [],
                reply_count: 0,
                pending: true,
                client_message_id: clientId,
                created_at: new Date().toISOString(),
            };
            return [...current.filter(item => item.id !== pendingId), pending];
        });
        try {
            const result = await createMessage(conversationId, body, clientId, attachments.map(attachment => attachment.id));
            setMessages(current => mergeMessages(current.filter(item => item.id !== pendingId), [result.message]));
            window.requestAnimationFrame(scrollToBottom);
        } catch (sendError) {
            setMessages(current => current.map(item => item.id === pendingId ? {...item, pending: false, send_error: true, sendError} : item));
            throw sendError;
        }
    }, [conversationId, currentUser, scrollToBottom]);

    const changeMessage = useCallback(async action => {
        try {
            await action();
            await refreshMessages(false);
        } catch (actionError) {
            setError(actionError);
            throw actionError;
        }
    }, [refreshMessages]);

    const actions = useMemo(() => ({
        edit: (message, body) => changeMessage(() => updateMessage(message.id, body, message.version)),
        remove: message => changeMessage(() => deleteMessage(message.id)),
        react: (message, emoji, reacted) => changeMessage(() => reacted ? removeReaction(message.id, emoji) : addReaction(message.id, emoji)),
        retry: message => sendMessage(message.body, message.attachments || [], message.client_message_id),
    }), [changeMessage, sendMessage]);

    const handleScroll = useCallback(event => {
        const element = event.currentTarget;
        const bottom = element.scrollHeight - element.scrollTop - element.clientHeight < 80;
        atBottomRef.current = bottom;
        setAtBottom(bottom);
        if (bottom) setNewMessageNotice(false);
    }, []);

    return {
        messages,
        loading,
        hasMore,
        error,
        setError,
        newMessageNotice,
        timelineRef,
        refreshMessages,
        loadOlder,
        sendMessage,
        actions,
        handleScroll,
        scrollToBottom,
    };
}
